import { useCallback, useState } from 'react';
import { IcoClose } from './common/Icons.jsx';
import styles from './MoveTabSheet.module.css';
import { useAtrasCierra } from '../lib/atras.js';

/**
 * Confirmación propia, en lugar de `window.confirm`.
 *
 * El diálogo nativo del WebView sale en otra tipografía, con "OK/Cancelar"
 * en el idioma del sistema, bloquea la pantalla entera y en Android a veces
 * se dibuja detrás del teclado. Esta hoja dice qué va a pasar en una frase,
 * pone el verbo en el botón ("Vaciar", no "OK") y en teléfono se ancla
 * abajo, donde está el pulgar.
 */
export default function ConfirmSheet({ open, titulo, texto, confirmar = 'Confirmar', cancelar = 'Cancelar', peligro = false, onConfirmar, onClose }) {
  useAtrasCierra(open, onClose);
  if (!open) return null;
  return (
    <div className={styles.scrim} role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet} role="alertdialog" aria-modal="true" aria-labelledby="confirm-titulo">
        <div className={styles.head}>
          <h2 className={styles.title} id="confirm-titulo">{titulo}</h2>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar"><IcoClose size={16} /></button>
        </div>
        {texto && <p className={styles.sub} style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{texto}</p>}
        <div className={styles.acciones}>
          <button className="btn btn--ghost" onClick={onClose} autoFocus>{cancelar}</button>
          <button className={`btn ${peligro ? 'btn--danger' : 'btn--primary'}`} onClick={onConfirmar}>{confirmar}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * `const { pedir, Confirmacion } = useConfirmacion();`
 * `if (!(await pedir({ titulo: '¿Vaciar el carrito?', confirmar: 'Vaciar', peligro: true }))) return;`
 *
 * Se usa como `window.confirm` —una promesa que resuelve true/false— para
 * que el código que decide siga leyéndose de corrido; `Confirmacion` se pinta
 * una vez en el JSX de quien lo usa.
 */
export function useConfirmacion() {
  const [pendiente, setPendiente] = useState(null);   // { opts, resolve }

  const pedir = useCallback((opts) => new Promise((resolve) => setPendiente({ opts, resolve })), []);

  const cerrar = (ok) => {
    pendiente?.resolve(ok);
    setPendiente(null);
  };

  const Confirmacion = (
    <ConfirmSheet
      open={!!pendiente}
      {...(pendiente?.opts ?? {})}
      onConfirmar={() => cerrar(true)}
      onClose={() => cerrar(false)}
    />
  );

  return { pedir, Confirmacion };
}
